<template>
  <transition name="bg-modal">
    <div
      v-if="visible"
      class="bg-modal-overlay"
      role="presentation"
      @click.self="close"
    >
      <div
        class="bg-login-modal bg-point-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-point-modal-title"
        @keydown.esc.prevent="close"
      >
        <header class="bg-login-modal__header">
          <h2 id="bg-point-modal-title" class="bg-point-modal__title">{{ $t('mypage.pointConvertTitle') }}</h2>
          <button
            type="button"
            class="bg-login-modal__close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <div class="bg-point-modal__body">
          <p v-if="loading" class="bg-point-modal__status">{{ $t('mypage.pointConvertLoading') }}</p>
          <template v-else>
            <ul class="bg-point-modal__summary">
              <li>
                <span>{{ $t('mypage.balance') }}</span>
                <strong>{{ formatBalance(balance) }}</strong>
              </li>
              <li>
                <span>{{ $t('mypage.point') }}</span>
                <strong>{{ formatPoint(point) }}</strong>
              </li>
            </ul>

            <form class="wallet-form" @submit.prevent="submit">
              <div class="wallet-form__field wallet-form__field--amount">
                <span class="wallet-form__label">{{ $t('mypage.pointConvertAmount') }} <em>({{ $t('wallet.required') }})</em></span>
                <input
                  v-model="amountDisplay"
                  type="text"
                  inputmode="numeric"
                  class="wallet-form__amount-input"
                  :placeholder="$t('mypage.pointConvertAmountPlaceholder')"
                  autocomplete="off"
                  :disabled="submitting"
                  @input="onAmountInput"
                >
              </div>

              <div class="wallet-form__quick">
                <button type="button" :disabled="submitting" @click="addAmount(1000)">1,000</button>
                <button type="button" :disabled="submitting" @click="addAmount(5000)">5,000</button>
                <button type="button" :disabled="submitting" @click="addAmount(10000)">10,000</button>
                <button type="button" :disabled="submitting" @click="setAllPoint">{{ $t('mypage.pointConvertAll') }}</button>
                <button type="button" class="wallet-form__quick-reset" :disabled="submitting" @click="resetAmount">{{ $t('common.refresh') }}</button>
              </div>

              <p v-if="errorMessage" class="bg-point-modal__error" role="alert">{{ errorMessage }}</p>

              <div class="wallet-form__actions wallet-form__actions--solo">
                <button
                  type="submit"
                  class="wallet-form__submit"
                  :disabled="submitting || loading"
                >
                  {{ submitting ? $t('mypage.pointConvertSubmitting') : $t('mypage.pointConvertSubmit') }}
                </button>
              </div>
            </form>

            <ul class="wallet-form__notes">
              <li>{{ $t('mypage.pointConvertNote1') }}</li>
              <li>{{ $t('mypage.pointConvertNote2') }}</li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { GameService, WalletService } from '@/services'
import { formatMoney, formatPointAmount } from '@/utils/format'

export default {
  name: 'BlackGoldPointConvertModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      submitting: false,
      balance: 0,
      point: 0,
      amount: 0,
      amountDisplay: '',
      errorMessage: ''
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.resetForm()
        this.loadBalances()
      }
    }
  },
  methods: {
    formatPoint (value) {
      return `${formatPointAmount(value)} P`
    },
    close () {
      if (this.submitting) return
      this.$emit('close')
    },
    resetForm () {
      this.amount = 0
      this.amountDisplay = ''
      this.errorMessage = ''
      this.submitting = false
    },
    async loadBalances () {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await GameService.getBalanceApi()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.balance = Number(body.data.balance) || 0
          this.point = Number(body.data.point) || 0
        } else {
          this.errorMessage = (body && body.message) ? body.message : this.$t('mypage.pointConvertLoadError')
        }
      } catch (err) {
        this.errorMessage = this.$t('mypage.pointConvertLoadError')
      } finally {
        this.loading = false
      }
    },
    onAmountInput () {
      const digits = String(this.amountDisplay || '').replace(/[^\d]/g, '')
      this.amount = digits ? Number(digits) : 0
      this.amountDisplay = digits ? formatMoney(this.amount) : ''
      this.errorMessage = ''
    },
    addAmount (value) {
      const next = (Number(this.amount) || 0) + Number(value)
      this.amount = next
      this.amountDisplay = formatMoney(next)
      this.errorMessage = ''
    },
    setAllPoint () {
      const max = Math.floor(Number(this.point) || 0)
      this.amount = max
      this.amountDisplay = max ? formatMoney(max) : ''
      this.errorMessage = ''
    },
    resetAmount () {
      this.amount = 0
      this.amountDisplay = ''
      this.errorMessage = ''
    },
    async submit () {
      if (this.submitting || this.loading) return

      const amount = Number(this.amount) || 0
      if (amount <= 0) {
        this.errorMessage = this.$t('mypage.pointConvertAmountRequired')
        return
      }
      const maxPoint = Math.floor(Number(this.point) || 0)
      if (amount > maxPoint) {
        this.errorMessage = this.$t('mypage.pointConvertAmountExceed')
        return
      }

      this.submitting = true
      this.errorMessage = ''
      try {
        const res = await WalletService.compRequest({ amount })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          this.resetAmount()
          await this.loadBalances()
          this.$emit('success')
          this.$emit('close')
          return
        }
        this.errorMessage = (body && body.message) ? body.message : this.$t('mypage.pointConvertFail')
      } catch (err) {
        this.errorMessage = this.$t('mypage.pointConvertFail')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
